import { Artwork, ArtworkSource } from "@/types/artworksData";

export const PLACEHOLDER_IMPORT_MESSAGE =
  "Metadata imported, model unavailable or too complex. Replace media to render as 3D.";

export type ImportedMetadataKey =
  | "country"
  | "subNationalAuthority"
  | "assetName"
  | "assetType"
  | "location"
  | "managingInstitution"
  | "shortDescription"
  | "category"
  | "subjects"
  | "medium"
  | "aggregator"
  | "creationDate"
  | "identifiers"
  | "extent"
  | "isPartOf"
  | "collectionName"
  | "firstPublishedAt"
  | "lastProviderUpdateAt"
  | "intendedReuse"
  | "rightStatement";

export const IMPORTED_METADATA_FIELDS: Array<{
  key: ImportedMetadataKey;
  label: string;
  multiline?: boolean;
}> = [
  { key: "country", label: "Country" },
  { key: "subNationalAuthority", label: "Sub-national authority" },
  { key: "assetName", label: "Name of asset" },
  { key: "assetType", label: "Type of asset" },
  { key: "location", label: "Location" },
  { key: "managingInstitution", label: "Managing institution" },
  { key: "shortDescription", label: "Short description", multiline: true },
  { key: "category", label: "Category" },
  { key: "subjects", label: "Subjects", multiline: true },
  { key: "medium", label: "Medium", multiline: true },
  { key: "aggregator", label: "Aggregator" },
  { key: "creationDate", label: "Creation date" },
  { key: "identifiers", label: "Identifiers", multiline: true },
  { key: "extent", label: "Extent", multiline: true },
  { key: "isPartOf", label: "Is part of", multiline: true },
  { key: "collectionName", label: "Collection name" },
  { key: "firstPublishedAt", label: "First published on Europeana" },
  { key: "lastProviderUpdateAt", label: "Last provider update" },
  { key: "intendedReuse", label: "Intended reuse" },
  { key: "rightStatement", label: "Right statement" },
];

export const PSEUDO_MOM_DETAIL_IMPORTED_METADATA_KEYS: ImportedMetadataKey[] = [
  "country",
  "subNationalAuthority",
  "assetType",
  "location",
  "managingInstitution",
  "category",
  "intendedReuse",
];

export const PSEUDO_MOM_READ_IMPORTED_METADATA_KEYS: ImportedMetadataKey[] = [
  "country",
  "subNationalAuthority",
  "assetType",
  "location",
  "managingInstitution",
  "category",
  "intendedReuse",
];

export const PSEUDO_MOM_EDIT_IMPORTED_METADATA_KEYS: ImportedMetadataKey[] = [
  "country",
  "subNationalAuthority",
  "assetType",
  "location",
  "managingInstitution",
  "category",
  "creationDate",
];

const PSEUDO_MOM_INTENDED_REUSE_TAGS = [
  "Knowledge",
  "Infotainment",
  "Creativity",
  "Curation",
];

const resolvePseudoMoMIntendedReuseFromTags = (tags?: string | null): string => {
  const entries = String(tags || "")
    .split(/[,;\n|]/)
    .map((entry) => entry.trim().toLowerCase())
    .filter(Boolean);
  const matchedTag = PSEUDO_MOM_INTENDED_REUSE_TAGS.find((tag) => entries.includes(tag.toLowerCase()));
  return matchedTag || "";
};

export const getImportedMetadata = (source?: ArtworkSource | null): Record<string, string> => {
  const metadata = source?.europeanaMetadata || {};
  return IMPORTED_METADATA_FIELDS.reduce<Record<string, string>>((acc, field) => {
    const value = String((metadata as any)[field.key] || "").trim();
    if (value) acc[field.key] = value;
    return acc;
  }, {});
};

export const hasImportedMetadata = (source?: ArtworkSource | null): boolean =>
  Object.keys(getImportedMetadata(source)).length > 0;

export const isPlaceholderImport = (source?: ArtworkSource | null): boolean =>
  source?.externalMediaStatus?.status === "placeholder-requires-replacement";

export const isImageLikeUrl = (value?: string | null): boolean => {
  if (!value) return false;
  if (/^data:image\//i.test(value)) return true;
  return /\.(jpe?g|png|gif|webp|svg|bmp|tiff?|avif)(?:[?#]|$)/i.test(value);
};

export const isPlaceholderArtwork = (artwork?: Partial<Artwork> | null): boolean =>
  Boolean(
    isPlaceholderImport(artwork?.source) ||
    artwork?.artworkData?.runtimeFileSizeSource === "external-placeholder" ||
    ((artwork as any)?.importable === false &&
      String((artwork as any)?.importUnavailableCode || "").toLowerCase() === "media_unavailable")
  );

const normalizeComparable = (value?: unknown): string =>
  String(Array.isArray(value) ? value[0] || "" : value || "").trim().toLowerCase();

const URL_LIKE_PATTERN = /^(https?:\/\/|www\.|[a-z][a-z0-9+.-]*:)/i;
const OPAQUE_REFERENCE_PATTERN = /^(Q\d+|\d+)$/i;

export const isUrlLikeMetadataValue = (value?: string | null): boolean =>
  URL_LIKE_PATTERN.test(String(value || "").trim());

export const humanizeMetadataUri = (value?: string | null): string => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (!isUrlLikeMetadataValue(raw)) return raw;

  try {
    const parsed = new URL(raw);
    const tail = parsed.hash || parsed.pathname.split("/").filter(Boolean).pop() || "";
    const decoded = decodeURIComponent(tail.replace(/^#/, ""));
    const humanized = decoded
      .replace(/\.[a-z0-9]+$/i, "")
      .replace(/[_-]+/g, " ")
      .trim();
    return /[a-z0-9]/i.test(humanized) ? humanized : "";
  } catch (_error) {
    const tail = raw.split(/[/#]/).filter(Boolean).pop() || "";
    const humanized = decodeURIComponent(tail)
      .replace(/\.[a-z0-9]+$/i, "")
      .replace(/[_-]+/g, " ")
      .trim();
    return /[a-z0-9]/i.test(humanized) ? humanized : "";
  }
};

const normalizeEntityUriKey = (value?: unknown): string => {
  const raw = String(Array.isArray(value) ? value[0] || "" : value || "").trim();
  if (!raw) return "";
  return raw
    .replace(/^https?:\/\/www\.wikidata\.org\/wiki\//i, "https://www.wikidata.org/entity/")
    .replace(/\/+$/, "")
    .toLowerCase();
};

const uriTailKey = (value?: unknown): string => {
  const raw = String(Array.isArray(value) ? value[0] || "" : value || "").trim();
  if (!raw) return "";
  try {
    const parsed = new URL(raw.startsWith("www.") ? `https://${raw}` : raw);
    return decodeURIComponent((parsed.hash || parsed.pathname.split("/").filter(Boolean).pop() || "").replace(/^#/, "")).toLowerCase();
  } catch (_error) {
    return decodeURIComponent(raw.split(/[/#]/).filter(Boolean).pop() || "").toLowerCase();
  }
};

const extractStringValues = (value?: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.flatMap((entry) => extractStringValues(entry));
  }
  if (typeof value === "string") return [value];
  if (typeof value === "object") {
    const candidate = (value as any).id || (value as any)["@id"] || (value as any).uri || (value as any).about || (value as any).value;
    return candidate ? extractStringValues(candidate) : [];
  }
  return [];
};

const extractContextPreferredLabel = (context?: any): string => {
  const prefLabel = context?.prefLabel;
  if (!prefLabel) return "";

  if (typeof prefLabel === "string") return prefLabel.trim();

  if (Array.isArray(prefLabel)) {
    const english = prefLabel.find((entry) => String(entry?.lang || "").toLowerCase().startsWith("en") && String(entry?.value || "").trim());
    const first = english || prefLabel.find((entry) => String(entry?.value || "").trim());
    return String(first?.value || "").trim();
  }

  if (typeof prefLabel === "object") {
    const english = (prefLabel as any).en;
    if (Array.isArray(english)) return String(english.find(Boolean) || "").trim();
    if (typeof english === "string") return english.trim();
    for (const value of Object.values(prefLabel)) {
      if (Array.isArray(value)) {
        const first = value.find(Boolean);
        if (first) return String(first).trim();
      } else if (typeof value === "string" && value.trim()) {
        return value.trim();
      }
    }
  }

  return "";
};

const buildContextEntityLabelMap = (contextEntities?: unknown): Map<string, string> => {
  const map = new Map<string, string>();
  if (!Array.isArray(contextEntities)) return map;

  const addKey = (key: unknown, label: string) => {
    const normalized = normalizeEntityUriKey(key);
    if (normalized && !map.has(normalized)) map.set(normalized, label);
    const tail = uriTailKey(key);
    if (tail && !map.has(tail)) map.set(tail, label);
  };

  contextEntities.forEach((context: any) => {
    const label = extractContextPreferredLabel(context);
    if (!label || isUrlLikeMetadataValue(label)) return;

    [
      context.uri,
      context.referenceUri,
      context.sameAs,
      context.exactMatch,
      context.broader,
      context.related,
      context.about,
      context.id,
      context["@id"],
    ].flatMap((value) => extractStringValues(value)).forEach((key) => addKey(key, label));
  });

  return map;
};

const isOpaqueReferenceValue = (value?: string | null): boolean =>
  OPAQUE_REFERENCE_PATTERN.test(String(value || "").trim());

const splitMetadataEntries = (value?: string | null): string[] =>
  String(value || "")
    .split(/\r?\n|,/)
    .map((entry) => entry.trim())
    .filter(Boolean);

const resolvePseudoMoMMetadataEntry = (entry: string, contextLabelMap: Map<string, string>): string => {
  const normalized = normalizeEntityUriKey(entry);
  const resolved = normalized ? contextLabelMap.get(normalized) : "";
  if (resolved) return resolved;

  const tail = uriTailKey(entry);
  const tailResolved = tail ? contextLabelMap.get(tail) : "";
  if (tailResolved) return tailResolved;

  if (isUrlLikeMetadataValue(entry)) {
    const humanized = humanizeMetadataUri(entry);
    return humanized && !isOpaqueReferenceValue(humanized) ? humanized : "";
  }

  return isOpaqueReferenceValue(entry) ? "" : entry;
};

const isNoisyDateReferenceEntry = (value?: string | null): boolean => {
  const raw = String(value || "").trim();
  if (!raw) return true;
  if (isUrlLikeMetadataValue(raw) || raw.startsWith("#")) return true;
  if (/^\[[^\]]+\]$/.test(raw)) return true;
  return /^[0-9xX?*\s._/-]+$/.test(raw);
};

const isPreferredDateLabel = (value?: string | null): boolean => {
  const raw = String(value || "").trim();
  if (!raw || isNoisyDateReferenceEntry(raw)) return false;
  return /[a-z]/i.test(raw);
};

const normalizePseudoMoMDateValue = (
  value: string,
  contextLabelMap: Map<string, string>
): string => {
  const entries = splitMetadataEntries(value);
  if (entries.length === 0) return "";

  const resolvedEntries = Array.from(
    new Set(entries.map((entry) => resolvePseudoMoMMetadataEntry(entry, contextLabelMap)).filter(Boolean))
  );
  const preferredLabels = resolvedEntries.filter((entry) => isPreferredDateLabel(entry));
  if (preferredLabels.length > 0) {
    return preferredLabels.join("\n");
  }

  return resolvedEntries
    .filter((entry) => {
      const raw = String(entry || "").trim();
      return raw && !isUrlLikeMetadataValue(raw) && !raw.startsWith("#") && !/^\[[^\]]+\]$/.test(raw);
    })
    .join("\n");
};

export const firstMetadataParagraph = (value?: string | null): string => {
  return String(value || "")
    .split(/\r?\n\s*\r?\n|\r?\n/)
    .map((entry) => entry.trim())
    .find(Boolean) || "";
};

export const descriptionParagraphs = (description?: string | string[] | null): string[] => {
  const values = Array.isArray(description) ? description : [description || ""];
  return values
    .flatMap((value) => String(value || "").split(/\r?\n\s*\r?\n/))
    .map((value) => value.trim())
    .filter(Boolean);
};

export const normalizePseudoMoMReadValue = (
  field: ImportedMetadataKey,
  value: string,
  contextLabelMap: Map<string, string>
): string => {
  if (field === "shortDescription") {
    return firstMetadataParagraph(value);
  }
  if (field === "creationDate") {
    return normalizePseudoMoMDateValue(value, contextLabelMap);
  }

  const entries = splitMetadataEntries(value);
  if (entries.length === 0) return "";

  return Array.from(
    new Set(entries.map((entry) => resolvePseudoMoMMetadataEntry(entry, contextLabelMap)).filter(Boolean))
  ).join("\n");
};

export const normalizePseudoMoMMetadataEditValue = (
  field: ImportedMetadataKey,
  value?: string | null,
  contextEntities?: unknown
): string => {
  if (field !== "creationDate") {
    return String(value || "");
  }
  return normalizePseudoMoMDateValue(String(value || ""), buildContextEntityLabelMap(contextEntities));
};

export const normalizePseudoMoMDisplayYear = (
  value?: string | null,
  contextEntities?: unknown
): string => normalizePseudoMoMDateValue(String(value || ""), buildContextEntityLabelMap(contextEntities));

export const isEuropeanaImportSource = (source?: ArtworkSource | null): boolean => {
  const sourceName = source?.sourceName?.toLowerCase();
  const provider = source?.provider?.toLowerCase();
  const publisher = source?.publisher?.toLowerCase();
  return sourceName === "europeana" || provider === "europeana" || publisher === "europeana";
};

export const isEuropeanaSource = (source?: ArtworkSource | null): boolean =>
  isEuropeanaImportSource(source) || Boolean(source?.europeanaMetadata);

export const resolveEuropeanaProviderDisplay = (source?: ArtworkSource | null): string => {
  if (!isEuropeanaSource(source)) return source?.provider || "";
  const metadata = source?.europeanaMetadata || {};
  const candidates = [
    metadata.managingInstitution,
    source?.provider,
    source?.publisher,
  ];
  return candidates
    .map((candidate) => String(candidate || "").trim())
    .find((candidate) => candidate && candidate.toLowerCase() !== "europeana") || "";
};

export const resolveEuropeanaImportSourceDisplay = (source?: ArtworkSource | null): string => {
  return isEuropeanaImportSource(source) ? "Europeana" : "";
};

export type ImportedMetadataDisplayRow = {
  key: ImportedMetadataKey;
  label: string;
  value: string;
};

export const getImportedMetadataDisplayRows = ({
  source,
  artworkTitle,
  artworkDescription,
  artworkYear,
  fieldKeys,
  pseudoMoMReadMode = false,
  contextEntities,
  artworkTags,
}: {
  source?: ArtworkSource | null;
  artworkTitle?: string | null;
  artworkDescription?: string | string[] | null;
  artworkYear?: string | null;
  fieldKeys?: ImportedMetadataKey[];
  pseudoMoMReadMode?: boolean;
  contextEntities?: unknown;
  artworkTags?: string | null;
}): ImportedMetadataDisplayRow[] => {
  const metadata = getImportedMetadata(source);
  if (Object.keys(metadata).length === 0 && !pseudoMoMReadMode) return [];
  const allowedFieldKeys = fieldKeys ? new Set(fieldKeys) : (pseudoMoMReadMode ? new Set(PSEUDO_MOM_READ_IMPORTED_METADATA_KEYS) : null);
  const contextLabelMap = pseudoMoMReadMode ? buildContextEntityLabelMap(contextEntities) : new Map<string, string>();

  return IMPORTED_METADATA_FIELDS.reduce<ImportedMetadataDisplayRow[]>((rows, field) => {
    if (allowedFieldKeys && !allowedFieldKeys.has(field.key)) return rows;
    let value = pseudoMoMReadMode
      ? normalizePseudoMoMReadValue(field.key, metadata[field.key], contextLabelMap)
      : metadata[field.key];
    if (pseudoMoMReadMode && field.key === "intendedReuse" && !value) {
      value = resolvePseudoMoMIntendedReuseFromTags(artworkTags);
    }
    if (!value) return rows;
    if (field.key === "assetName" && normalizeComparable(value) === normalizeComparable(artworkTitle)) return rows;
    if (field.key === "shortDescription" && normalizeComparable(value) === normalizeComparable(artworkDescription)) return rows;
    if (field.key === "creationDate" && normalizeComparable(value) === normalizeComparable(artworkYear)) return rows;

    rows.push({
      key: field.key,
      label: field.label,
      value,
    });
    return rows;
  }, []);
};

export const ImportedMetadataDetails = ({
  source,
  className = "",
  labelClassName = "",
  valueClassName = "",
  artworkTitle,
  artworkDescription,
  artworkYear,
  fieldKeys,
  hideHeading = false,
  pseudoMoMReadMode = false,
  contextEntities,
  artworkTags,
}: {
  source?: ArtworkSource | null;
  className?: string;
  labelClassName?: string;
  valueClassName?: string;
  artworkTitle?: string | null;
  artworkDescription?: string | string[] | null;
  artworkYear?: string | null;
  fieldKeys?: ImportedMetadataKey[];
  hideHeading?: boolean;
  pseudoMoMReadMode?: boolean;
  contextEntities?: unknown;
  artworkTags?: string | null;
}) => {
  const visibleFields = getImportedMetadataDisplayRows({
    source,
    artworkTitle,
    artworkDescription,
    artworkYear,
    fieldKeys,
    pseudoMoMReadMode,
    contextEntities,
    artworkTags,
  });
  if (visibleFields.length === 0) return null;

  return (
    <div className={className}>
      {!hideHeading && <p className={labelClassName || "mb-2"}>Imported metadata</p>}
      {visibleFields.map((field) => (
        <div key={field.key} className="mb-2">
          <p className={labelClassName || "mb-1"}>{field.label}</p>
          <p className={valueClassName || "mb-0"} style={{ whiteSpace: "pre-line" }}>
            {field.value}
          </p>
        </div>
      ))}
    </div>
  );
};
